import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { PULSEAI_VARIANTS as SVG_VARIANTS } from "./PulseAIVisuals";

const IMAGE_BASE = `${API}/pulseai-assets`;

export const PULSEAI_METADATA = {
  a_robot: {
    label: "Humanoidný AI Android",
    style: "Cinematic / Blade Runner",
    desc: "Hyperrealistický portrét futuristického androida. Chromovaná tvár, žiariace cyan oči, fialové neon akcenty. Inšpirované Blade Runner 2049 + Ex Machina.",
  },
  b_brain: {
    label: "Neurónový mozog",
    style: "Ethereal / Glowing",
    desc: "3D vizualizácia neurónovej siete — svetelné synaptické uzly, pulzujúca iridescentná energia. Reprezentuje čistú AI inteligenciu.",
  },
  c_owl: {
    label: "PulseOwl Maskot",
    style: "Premium Character",
    desc: "Cyberpunk sova maskot s neon perím a holografickými okuliarmi. Pixar-quality rendering, vysoká memorabilita pre B2C.",
  },
  d_orb: {
    label: "Pulse Orb",
    style: "Cinematic / Cosmic",
    desc: "Luminiscenčná plazma guľa s rotujúcimi prstencami partikulov. Kombinácia Gemini orb + Iron Man arc reactor. Minimal, premium.",
  },
  e_human: {
    label: "Alex — AI Persona",
    style: "Editorial / Trustworthy",
    desc: "Magazin cover portrét profesionálneho tech founder-a. Cinematic studio lighting, personal touch pre B2B trust signál.",
  },
  f_hybrid: {
    label: "Hybrid: Orb + Neural",
    style: "Award-Winning / Premium",
    desc: "Radujúci sa energetický orb s neurónovými úponkami ako slnečnými lúčmi. Najpremiumnejší variant, ideálny pre premium brand.",
  },
  g_aria: {
    label: "Aria — AI Oracle",
    style: "Cyberpunk / Breathtaking",
    desc: "Dychberúca bohyňa AI s iridescentnou pokožkou, bioluminiscenčnými circuit tattoos a holografickým vizorom. Ghost in the Shell meets Horizon.",
  },
  h_cipher: {
    label: "Cipher — All-Seeing Eye",
    style: "Mesmerizing / Iconic",
    desc: "Kolosálne všetko-vidiace AI oko s iris z neurónových obvodov a galaktickou zrenicou. Hypnotické, nezabudnuteľné.",
  },
  i_nexus: {
    label: "Nexus — Crystal Core",
    style: "Sacred / Legendary",
    desc: "Kryštálový monolit s iridescentnou svetelnou energiou a posvätnou geometriou. 2001 Space Odyssey stretáva legendárny drahokam.",
  },
};

// Renders a PulseAI brand avatar. If customUrl is provided, it overrides the variant.
export const PulseAIImage = ({ variantKey, customUrl, size = 200, rounded = true, className = "" }) => {
  const [imgError, setImgError] = useState(false);
  const SVGCmp = SVG_VARIANTS[variantKey]?.Component;
  const imgUrl = customUrl || `${IMAGE_BASE}/${variantKey}.png`;

  if (imgError && SVGCmp && !customUrl) {
    return (
      <div className={className} style={{ width: size, height: size }}>
        <SVGCmp size={size} />
      </div>
    );
  }

  return (
    <img
      src={imgUrl}
      alt={PULSEAI_METADATA[variantKey]?.label || variantKey}
      width={size}
      height={size}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: rounded ? "50%" : "0",
        display: "block",
      }}
      onError={() => setImgError(true)}
      loading="lazy"
    />
  );
};

// Hook: returns active brand for current viewer.
// Priority: logged-in enterprise user's custom brand > global admin-selected variant.
// Returns: { key, imageUrl, name, suffix, isCustom }
export const usePulseAIActive = () => {
  const { user } = useAuth();
  const [state, setState] = useState(() => {
    let key = "f_hybrid";
    try {
      key = localStorage.getItem("pulseai_active") || "f_hybrid";
    } catch {}
    return { key, imageUrl: null, name: "PulseAI", suffix: " Engine™", isCustom: false };
  });

  useEffect(() => {
    let cancelled = false;
    const loadGlobal = () =>
      axios.get(`${API}/pulseai/active`).then((r) => {
        if (cancelled) return;
        const k = r.data?.key || "f_hybrid";
        setState({ key: k, imageUrl: null, name: "PulseAI", suffix: " Engine™", isCustom: false });
        try { localStorage.setItem("pulseai_active", k); } catch {}
      }).catch(() => {});

    if (user) {
      axios
        .get(`${API}/pulseai/custom-brand/my`)
        .then((r) => {
          if (cancelled) return;
          const d = r.data || {};
          const hasCustom = d.enabled && (d.has_custom_image || d.custom_name);
          if (hasCustom) {
            // If user has only a name (no image yet), fetch global variant for avatar
            if (!d.has_custom_image) {
              axios.get(`${API}/pulseai/active`).then((res2) => {
                if (cancelled) return;
                setState({
                  key: res2.data?.key || "f_hybrid",
                  imageUrl: null,
                  name: d.custom_name || "PulseAI",
                  suffix: d.custom_suffix || " Engine™",
                  isCustom: true,
                });
              }).catch(() => {
                setState({
                  key: "f_hybrid",
                  imageUrl: null,
                  name: d.custom_name || "PulseAI",
                  suffix: d.custom_suffix || " Engine™",
                  isCustom: true,
                });
              });
            } else {
              setState({
                key: "custom",
                // Backend returns image_url already prefixed with '/api/...'. API const is
                // BACKEND_URL + '/api', so strip the leading '/api' from image_url to avoid
                // producing '/api/api/...'
                imageUrl: d.image_url
                  ? `${API}${d.image_url.replace(/^\/api/, "")}`
                  : null,
                name: d.custom_name || "PulseAI",
                suffix: d.custom_suffix || " Engine™",
                isCustom: true,
              });
            }
          } else {
            loadGlobal();
          }
        })
        .catch(() => loadGlobal());
    } else {
      loadGlobal();
    }

    return () => { cancelled = true; };
  }, [user]);

  return state;
};
